import { Component, ElementRef, ViewChild } from '@angular/core';
import { MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SlickCarouselModule } from 'ngx-slick-carousel';
import { CommonModule } from '@angular/common';
import { InputGeneralComponent } from 'app/components/input-general/input-general.component';
import { RouterModule } from '@angular/router';
import {
    FormBuilder,
    FormGroup,
    FormsModule,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms';
import { HomePageService } from '../home.service';
import { FooterComponent } from 'app/components/footer/footer.component';
import { HeaderComponent } from 'app/components/header/header.component';
import { ToastrService } from 'ngx-toastr';
import { ForbiddenCharsDirective } from 'app/shared/forbidden-chars.directive';

@Component({
    selector: 'contact-us',
    templateUrl: './contact-us.component.html',
    styleUrls: ['./contact-us.component.scss'],
    standalone: true,
    imports: [
        HeaderComponent,
        FooterComponent,
        MatDialogModule,
        MatButtonModule,
        MatIconModule,
        TranslateModule,
        SlickCarouselModule,
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        RouterModule,
        InputGeneralComponent,
        ForbiddenCharsDirective,
    ],
})
export class ContactUsComponent {
    contactUsForm: FormGroup;
    submitted: boolean = false;
    disableSubmitBtn: boolean = false;
    isRequiredError: any;
    invalidEmailError: any;
    digitOnlyError: any;
    phoneNoMinMaxError: any;
    isRequired: any;
    closeTostr: string;
    contactUsSuccessMsg: string;
    selectedLanguage: any;

    @ViewChild('firstName') firstName: ElementRef<HTMLInputElement>;
    @ViewChild('lastName') lastName: ElementRef<HTMLInputElement>;
    @ViewChild('email') email: ElementRef<HTMLInputElement>;
    @ViewChild('phoneNumber') phoneNumber: ElementRef<HTMLInputElement>;
    @ViewChild('company') company: ElementRef<HTMLInputElement>;
    @ViewChild('message') message: ElementRef<HTMLInputElement>;

    constructor(
        private fb: FormBuilder,
        private homepageService: HomePageService,
        private translate: TranslateService,
        private toastr: ToastrService
    ) {}

    ngOnInit(): void {
        this.contactUsForm = this.fb.group({
            firstName: [''],
            lastName: [''],
            email: [''],
            phoneNumber: [''],
            company: [''],
            message: [''],
        });
        this.selectedLanguage = localStorage.getItem('language');
    }

    get f() {
        return this.contactUsForm.controls;
    }

    addValidators() {
        this.contactUsForm
            .get('firstName')
            .setValidators([
                Validators.required,
                Validators.pattern('^[a-zA-Z ]*$'),
            ]);
        this.contactUsForm.get('firstName').updateValueAndValidity();

        this.contactUsForm
            .get('lastName')
            .setValidators([
                Validators.required,
                Validators.pattern('^[a-zA-Z ]*$'),
            ]);
        this.contactUsForm.get('lastName').updateValueAndValidity();

        this.contactUsForm
            .get('phoneNumber')
            .setValidators([
                Validators.required,
                Validators.minLength(6),
                Validators.maxLength(11),
            ]);
        this.contactUsForm.get('phoneNumber').updateValueAndValidity();

        this.contactUsForm
            .get('email')
            .setValidators([
                Validators.required,
                Validators.pattern('^[a-z0-9._%+-]+@[a-z0-9.-]+\\.[a-z]{2,4}$'),
            ]);
        this.contactUsForm.get('email').updateValueAndValidity();

        this.contactUsForm.get('company').setValidators([Validators.required]);
        this.contactUsForm.get('company').updateValueAndValidity();

        this.contactUsForm
            .get('message')
            .setValidators([
                Validators.required,
                Validators.minLength(10),
                Validators.maxLength(200),
            ]);
        this.contactUsForm.get('message').updateValueAndValidity();
    }

    removeValidators() {
        this.contactUsForm.get('firstName').clearValidators();
        this.contactUsForm.get('firstName').updateValueAndValidity();

        this.contactUsForm.get('lastName').clearValidators();
        this.contactUsForm.get('lastName').updateValueAndValidity();

        this.contactUsForm.get('phoneNumber').clearValidators();
        this.contactUsForm.get('phoneNumber').updateValueAndValidity();

        this.contactUsForm.get('email').clearValidators();
        this.contactUsForm.get('email').updateValueAndValidity();

        this.contactUsForm.get('company').clearValidators();
        this.contactUsForm.get('company').updateValueAndValidity();

        this.contactUsForm.get('message').clearValidators();
        this.contactUsForm.get('message').updateValueAndValidity();
    }

    switchedLanguage(lan: any) {
        this.selectedLanguage = lan;
    }

    getTranslations() {
        this.translate
            .get('CONTACT_US.ENQUIRY_SUCCESS')
            .subscribe((data: any) => {
                this.contactUsSuccessMsg = data;
            });

        this.translate.get('COMMON.CLOSE').subscribe((data: any) => {
            this.closeTostr = data;
        });
    }

    onSubmit() {
        this.getTranslations();
        this.submitted = true;
        const userDetails = {
            firstName: this.contactUsForm.value.firstName,
            lastName: this.contactUsForm.value.lastName,
            phone: this.contactUsForm.value.phoneNumber,
            email: this.contactUsForm.value.email,
            company: this.contactUsForm.value.company,
            message: this.contactUsForm.value.message,
        };

        this.addValidators();

        if (this.contactUsForm.valid) {
            this.disableSubmitBtn = true;
            this.homepageService.submitContactUs(userDetails).subscribe({
                next: (data) => {
                    this.disableSubmitBtn = false;
                    this.toastr.success(this.contactUsSuccessMsg);
                    this.contactUsForm.reset();
                    this.removeValidators();
                },
                error: (error) => {
                    this.disableSubmitBtn = false;
                    this.toastr.error('error');
                },
            });
        } else {
            for (const key of Object.keys(this.f)) {
                if (this.f[key].invalid) {
                    if (key == 'firstName') {
                        this.firstName.nativeElement.focus();
                    } else if (key == 'lastName') {
                        this.lastName.nativeElement.focus();
                    } else if (key == 'email') {
                        this.email.nativeElement.focus();
                    } else if (key == 'phoneNumber') {
                        this.phoneNumber.nativeElement.focus();
                    } else if (key == 'company') {
                        this.company.nativeElement.focus();
                    } else {
                        this.message.nativeElement.focus();
                    }
                    break;
                }
            }
        }
    }

    getErrors(input: any, inputName?: any): Array<any> {
        let errors = [];

        if (this.f[input].touched || this.submitted) {
            if (this.f[input].errors?.required) {
                errors.push(inputName + ' ' + this.isRequired);
            }
        }
        return errors;
    }
}
